"use client";

import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";

const NodeRewardsBurnProgress = ({ nodeRewardsStats }) => {
  const [progressData, setProgressData] = useState({
    alreadyBurned: 0,
    pendingCandidates: 0,
    remainingAmount: 0,
    burnedPercentage: 0,
  });

  useEffect(() => {
    if (nodeRewardsStats) {
      const alreadyBurned = parseFloat(nodeRewardsStats.total_burned_from_events || 0);
      const pendingCandidates = parseFloat(nodeRewardsStats.total_candidate_amount || 0);
      const remainingAmount = Math.max(0, pendingCandidates - alreadyBurned);
      
      // Calculate burned as percentage of pending candidates
      const burnedPercentage = pendingCandidates > 0 ? (alreadyBurned / pendingCandidates) * 100 : 0;

      setProgressData({
        alreadyBurned,
        pendingCandidates,
        remainingAmount,
        burnedPercentage,
      });
    }
  }, [nodeRewardsStats]);

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return parseFloat(num).toLocaleString();
  };

  const formatPercent = (percent) => {
    // Show at least 4 decimal places if the number is very small
    if (percent < 0.01) return percent.toFixed(4) + '%';
    return percent.toFixed(2) + '%';
  };

  if (!nodeRewardsStats) {
    return (
      <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6">
        <div className="animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 bg-[#23272e] rounded w-48"></div>
          </div>
          <div className="h-32 bg-[#23272e] rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#161a23] rounded-xl border border-[#2a2f38] p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            Node Rewards Burn Progress
          </h3>
          <p className="text-sm text-gray-400">
            Progress of burning node rewards tokens
          </p>
        </div>
        <Icon icon="ph:chart-pie-slice-bold" className="text-xl text-metaxseed-blue" />
      </div>

      {progressData.pendingCandidates === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 flex-grow">
          <Icon icon="ph:chart-pie-slice-duotone" className="text-4xl text-gray-600 mb-4" />
          <p className="text-gray-400">No burn progress data available</p>
        </div>
      ) : (
        <div className="space-y-6 flex flex-col flex-grow justify-between">
          {/* Progress Bar */}
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Burn Progress</span>
              <span className="text-white font-medium">{formatPercent(progressData.burnedPercentage)} Complete</span>
            </div>
            <div className="w-full bg-[#0f1419] rounded-full h-4 relative overflow-hidden">
              {/* Green (Already Burned) */}
              <div 
                className="bg-gradient-to-r from-green-500 to-green-400 h-full transition-all duration-300"
                style={{ width: `${Math.min(progressData.burnedPercentage, 100)}%` }}
              ></div>
              {/* Red (Pending Candidates) - shown as remaining space */}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Already Burned (Green) */}
            <div className="bg-[#0f1419] p-4 rounded-lg border border-green-500/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                <span className="text-xs text-gray-400">Already Burned</span>
              </div>
              <p className="text-lg font-bold text-green-400">
                {formatNumber(progressData.alreadyBurned)}
              </p>
              <p className="text-xs text-green-300">
                {formatPercent(progressData.burnedPercentage)} of allocation
              </p>
            </div>

            {/* Pending Candidates (Red) */}
            <div className="bg-[#0f1419] p-4 rounded-lg border border-red-500/20">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <span className="text-xs text-gray-400">Pending To Be Burnt</span>
              </div>
              <p className="text-lg font-bold text-red-400">
                {formatNumber(progressData.remainingAmount)}
              </p>
              <p className="text-xs text-red-300">
                {formatPercent(100 - progressData.burnedPercentage)} remaining
              </p>
            </div>
          </div>

          {/* Total Summary */}
          <div className="pt-4 border-t border-[#2a2f38]">
            <div className="text-center">
              <p className="text-xs text-gray-400 mb-1">Total Node Rewards Allocation</p>
              <p className="text-xl font-bold text-white">
                {formatNumber(progressData.pendingCandidates)}
              </p>
              <div className="flex items-center justify-center gap-4 mt-2 text-xs">
                <div className="flex items-center gap-1">
                  <Icon icon="ph:fire-bold" className="text-green-400" />
                  <span className="text-green-400">Burned: {formatNumber(progressData.alreadyBurned)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon icon="ph:clock-bold" className="text-red-400" />
                  <span className="text-red-400">Pending: {formatNumber(progressData.remainingAmount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NodeRewardsBurnProgress; 